import React, { useEffect, useState } from 'react';
import { X, Download, ExternalLink, FileText, Loader2 } from 'lucide-react';

/**
 * FilePreviewModal — in-app preview for receipts, W-9, COI, lien waivers,
 * and completion photos. Replaces target="_blank" navigation so the user
 * stays inside the project context (no back-button refresh).
 *
 * Auto-detects file kind from URL/filename extension:
 *   - image/* → <img>
 *   - pdf     → <iframe> (browser-native PDF viewer)
 *   - other   → download-only card with "Open in new tab" escape hatch
 *
 * v2.5.4.3: the API ingress sends `X-Frame-Options: DENY` on /api/files/*
 * which blocks PDFs (and any same-origin file) from rendering inside an
 * <iframe src=...>. Workaround: fetch the file via JS, wrap in a Blob, and
 * use the resulting object URL — blob: URLs are same-origin and not subject
 * to X-Frame-Options. Same trick applies to images for consistency, and
 * gives us a loading state for free.
 */
export default function FilePreviewModal({ url, filename, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setBlobUrl(null);
    setError(null);
    setLoading(true);
    if (!url) return;
    fetch(url, { credentials: 'omit' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Failed to load file');
        setLoading(false);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [url]);

  if (!url) return null;

  const lower = (filename || url).toLowerCase();
  const isImage = /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)(\?|$)/i.test(lower);
  const isPdf = /\.pdf(\?|$)/i.test(lower) || lower.includes('lien-waiver-pdf') || lower.includes('contract');
  const displayName = filename || decodeURIComponent(url.split('/').pop() || 'Document');
  const src = blobUrl || url;

  return (
    <div
      data-testid="file-preview-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.82)',
        backdropFilter: 'blur(6px)', zIndex: 70,
        display: 'flex', flexDirection: 'column', padding: 20,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <FileText className="w-5 h-5 text-white" style={{ flexShrink: 0 }} />
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </div>
        <a
          href={blobUrl || url}
          download={filename || true}
          className="inline-flex items-center gap-1.5 text-xs bg-white/10 text-white hover:bg-white/20 px-3 py-1.5 rounded-lg"
          style={{ textDecoration: 'none' }}
          data-testid="file-preview-download"
        >
          <Download className="w-4 h-4" /> Download
        </a>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs bg-white/10 text-white hover:bg-white/20 px-3 py-1.5 rounded-lg"
          style={{ textDecoration: 'none' }}
          title="Open in new tab"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
        <button
          onClick={onClose}
          className="inline-flex items-center justify-center text-white bg-white/10 hover:bg-white/20 rounded-lg"
          style={{ width: 36, height: 36 }}
          aria-label="Close"
          data-testid="file-preview-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div
        style={{
          flex: 1, background: '#0b1220', borderRadius: 12, overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.1)', position: 'relative',
        }}
      >
        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}>
            <Loader2 className="w-6 h-6 animate-spin" />
            <span style={{ marginLeft: 10, fontSize: 13 }}>Loading…</span>
          </div>
        )}
        {!loading && error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fecaca', textAlign: 'center', padding: 24 }}>
            <div>
              <p style={{ margin: 0, fontSize: 14 }}>Couldn't load preview: {error}</p>
              <p style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>Use the Download button above to save the file.</p>
            </div>
          </div>
        )}
        {!loading && !error && isImage && (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
            <img src={src} alt={displayName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </div>
        )}
        {!loading && !error && isPdf && (
          <iframe
            src={src}
            title={displayName}
            style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
          />
        )}
        {!loading && !error && !isImage && !isPdf && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#cbd5e1', padding: 24, textAlign: 'center' }}>
            <div>
              <FileText className="w-12 h-12 mx-auto mb-3" style={{ opacity: 0.5 }} />
              <p style={{ fontSize: 14, margin: 0 }}>This file can't be previewed in-app.</p>
              <p style={{ fontSize: 12, marginTop: 6, opacity: 0.7 }}>Use the Download button above to save it.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
